import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select, IconBell, IconBellOff, IconAlertTriangle } from '../atoms';
import { STATE_NAME_OPTIONS } from '../lib/us-states';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * StageTemplateEditor — the super-admin editor for the per-STATE appeal ladder
 * (appeal-lifecycle respec §2.5).
 *
 * One state at a time (state select), its forums as rows — each with an editable
 * name, an ordered event chip list (add / remove / toggle-notifiable), and a
 * Retire toggle (retire never delete — §2.5). "+ Add forum" appends a forum.
 * Reads the merged full grid from GET /api/org-settings/stage-templates and
 * persists edits via PUT (super_admin only, enforced server-side). Backed by
 * config/stage-templates.js — the FE renders whatever states the server returns.
 *
 * PLACEMENT NOTE (§4.4 divergence): the respec says stage templates get "a tab
 * beside State Rules". State Rules is a standalone PAGE in this app, not a tab
 * host, so per §2.5 ("lives in org settings") this ships as an Org Settings
 * SECTION — the §2.5 "org-settings item" — rather than a State-Rules tab.
 */

interface StageEvent {
  name: string;
  notifiable?: boolean;
}
interface Forum {
  name: string;
  events: StageEvent[];
  retired?: boolean;
}
interface StateTemplate {
  forums: Forum[];
}
type Templates = Record<string, StateTemplate>;

/** Deep clone so the draft never aliases the query cache. */
function cloneTemplates(t: Templates): Templates {
  const out: Templates = {};
  for (const [state, tmpl] of Object.entries(t)) {
    out[state] = {
      forums: (tmpl.forums || []).map((f) => ({
        name: f.name,
        events: (f.events || []).map((e) => ({ name: e.name, ...(e.notifiable ? { notifiable: true } : {}) })),
        ...(f.retired ? { retired: true } : {}),
      })),
    };
  }
  return out;
}

/** The per-forum strand map + aggregate counts from GET …/stage-templates/impact. */
interface ImpactCounts {
  states: number;
  counties: number;
  parcels: number;
  openAppeals: number;
  historicalRecords: number;
}
interface ImpactResponse {
  state: string;
  counts: ImpactCounts;
  forums: Record<string, { openAppeals: number; valueRows: number }>;
}

/** The diff of ONE state's ladder — what the §4.6 confirmation shows before save. */
interface StateDiff {
  state: string;
  renamed: { from: string; to: string }[];
  added: string[];
  retired: string[];   // forum names AS HISTORY KNOWS THEM (the persisted label) — the strand-lookup key
  restored: string[];
  eventChanges: { forum: string; added: string[]; removed: string[] }[];
  otherChanges: boolean; // the state changed but not structurally (e.g. a notifiable toggle)
}

/**
 * Compute the §4.6 diff by comparing the last-saved templates to the edited draft.
 *
 * Forums are matched by POSITION: the editor only renames-in-place, retires-in-place,
 * or APPENDS a forum (retire never delete — §2.5 — and there is no reorder), so index
 * alignment is exact. A forum past the persisted length is a newly-added forum.
 * `retired` carries the PERSISTED (original) label because that is what historical
 * parcel_values.forum rows — and the impact strand map — are keyed on; a rename in the
 * same save is surfaced separately in `renamed`.
 */
function computeStateDiff(state: string, persisted: StateTemplate, draft: StateTemplate): StateDiff {
  const p = persisted.forums || [];
  const d = draft.forums || [];
  const renamed: { from: string; to: string }[] = [];
  const added: string[] = [];
  const retired: string[] = [];
  const restored: string[] = [];
  const eventChanges: { forum: string; added: string[]; removed: string[] }[] = [];

  for (let i = 0; i < d.length; i++) {
    const df = d[i];
    const pf = p[i];
    if (!pf) { added.push(df.name); continue; }        // appended forum
    if (pf.name !== df.name) renamed.push({ from: pf.name, to: df.name });
    if (!!df.retired && !pf.retired) retired.push(pf.name);
    if (!df.retired && !!pf.retired) restored.push(pf.name);
    const pEvents = (pf.events || []).map((e) => e.name);
    const dEvents = (df.events || []).map((e) => e.name);
    const evAdded = dEvents.filter((n) => !pEvents.includes(n));
    const evRemoved = pEvents.filter((n) => !dEvents.includes(n));
    if (evAdded.length || evRemoved.length) eventChanges.push({ forum: df.name, added: evAdded, removed: evRemoved });
  }
  return { state, renamed, added, retired, restored, eventChanges, otherChanges: false };
}

/** True when a state's diff carries at least one STRUCTURAL change (forum/event). */
function hasStructuralChanges(diff: StateDiff): boolean {
  return diff.renamed.length > 0 || diff.added.length > 0 || diff.retired.length > 0
    || diff.restored.length > 0 || diff.eventChanges.length > 0;
}

/**
 * Every state whose ladder changed between the last-saved grid and the draft — a
 * state is included when its JSON differs (so nothing is hidden). A state that
 * changed but shows no structural diff (e.g. only a notifiable toggle) is flagged
 * `otherChanges` so the confirmation still lists it rather than rendering blank.
 */
function computeAllDiffs(persisted: Templates, draft: Templates): StateDiff[] {
  const out: StateDiff[] = [];
  for (const state of Object.keys(draft)) {
    const before = persisted[state] ?? { forums: [] };
    if (JSON.stringify(before) === JSON.stringify(draft[state])) continue; // unchanged
    const diff = computeStateDiff(state, before, draft[state]);
    if (!hasStructuralChanges(diff)) diff.otherChanges = true;
    out.push(diff);
  }
  return out;
}

export function StageTemplateEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const templatesQuery = useQuery<Templates>({
    queryKey: ['org-settings', 'stage-templates'],
    queryFn: ({ signal }) => api.get<Templates>('/api/org-settings/stage-templates', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<Templates | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newForum, setNewForum] = useState('');
  const [newEvent, setNewEvent] = useState<Record<number, string>>({}); // per-forum-index draft event name
  const [confirmOpen, setConfirmOpen] = useState(false);               // §4.6 confirmation gate
  const [addingState, setAddingState] = useState(false);               // §6.1 add-state form open
  const [newState, setNewState] = useState('');                        // the state being added
  const [copyFrom, setCopyFrom] = useState('');                        // base template to copy, '' = empty

  const persisted = templatesQuery.data;
  const value = draft ?? persisted ?? null;
  const dirty = draft !== null && persisted != null && JSON.stringify(draft) !== JSON.stringify(persisted);

  const stateKeys = value ? Object.keys(value) : [];
  const active = selectedState && stateKeys.includes(selectedState) ? selectedState : (stateKeys[0] ?? '');
  const forums = active && value ? value[active].forums : [];

  // §4.6 — the diff (last-saved → draft) + the blast-radius counts, resolved only
  // while the confirmation is open. The diff is computed for EVERY changed state so
  // nothing destructive is hidden; the impact counts + strand map are fetched for
  // the ACTIVE state (where edits are keyed), and the strand warning on a retired
  // forum reads that state's per-forum map.
  const diffs = confirmOpen && draft && persisted ? computeAllDiffs(persisted, draft) : [];
  const impactQuery = useQuery<ImpactResponse>({
    queryKey: ['org-settings', 'stage-templates', 'impact', active],
    queryFn: ({ signal }) => api.get<ImpactResponse>(`/api/org-settings/stage-templates/impact?state=${encodeURIComponent(active)}`, signal),
    enabled: confirmOpen && !!active,
    retry: false,
  });
  const impact = impactQuery.data;

  /** Apply a mutation to the active state's forums via a fresh cloned draft. */
  function editForums(mutate: (forums: Forum[]) => Forum[]) {
    if (!value || !active) return;
    const base = cloneTemplates(value);
    base[active] = { forums: mutate(base[active].forums) };
    setDraft(base);
  }

  function renameForum(idx: number, name: string) {
    editForums((fs) => fs.map((f, i) => (i === idx ? { ...f, name } : f)));
  }

  function toggleRetire(idx: number) {
    editForums((fs) => fs.map((f, i) => (i === idx ? { ...f, retired: !f.retired } : f)));
  }

  function addForum() {
    const name = newForum.trim();
    if (!name) return;
    editForums((fs) => [...fs, { name, events: [] }]);
    setNewForum('');
  }

  /** States without a ladder yet — the only ones "Add state" can offer. */
  const availableStates = STATE_NAME_OPTIONS.filter(
    (o) => !stateKeys.some((k) => k.toLowerCase() === String(o.value).toLowerCase()),
  );

  function cancelAddState() {
    setAddingState(false);
    setNewState('');
    setCopyFrom('');
  }

  /**
   * Add a state's ladder to the draft. Copying a base template clones its forums
   * (deep, so editing the new state never mutates the one it came from) and drops
   * any retired rung — a retirement is that state's history, not a starting shape.
   * The new state becomes active so the user lands in what they just created.
   */
  function addState() {
    const name = newState.trim();
    if (!name || !value) return;
    if (stateKeys.some((k) => k.toLowerCase() === name.toLowerCase())) return;
    const base = cloneTemplates(value);
    const source = copyFrom && base[copyFrom] ? base[copyFrom].forums : [];
    base[name] = {
      forums: source
        .filter((f) => !f.retired)
        .map((f) => ({ name: f.name, events: f.events.map((e) => ({ ...e })) })),
    };
    setDraft(base);
    setSelectedState(name);
    cancelAddState();
  }

  function addEvent(idx: number) {
    const name = (newEvent[idx] ?? '').trim();
    if (!name) return;
    editForums((fs) => fs.map((f, i) => {
      if (i !== idx) return f;
      if (f.events.some((e) => e.name.toLowerCase() === name.toLowerCase())) return f; // no dupes
      return { ...f, events: [...f.events, { name }] };
    }));
    setNewEvent((m) => ({ ...m, [idx]: '' }));
  }

  function removeEvent(idx: number, eventName: string) {
    editForums((fs) => fs.map((f, i) => (i === idx ? { ...f, events: f.events.filter((e) => e.name !== eventName) } : f)));
  }

  function toggleNotifiable(idx: number, eventName: string) {
    editForums((fs) => fs.map((f, i) => (
      i === idx
        ? { ...f, events: f.events.map((e) => (e.name === eventName ? { name: e.name, ...(e.notifiable ? {} : { notifiable: true }) } : e)) }
        : f
    )));
  }

  // The actual write — fired ONLY from the §4.6 confirmation's Confirm button, never
  // straight off the Save button. The full draft grid is sent (every state preserved);
  // on success the modal closes and the draft clears.
  async function confirmSave() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put('/api/org-settings/stage-templates', { stage_templates: draft });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'stage-templates'] });
      setDraft(null);
      setConfirmOpen(false);
      toast({ variant: 'success', message: 'Stage templates saved.' });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Save failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      gridColumn: '1 / -1',
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-2)', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Appeal stage templates</h3>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          {canEdit && dirty && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)} disabled={saving}>Reset</Button>
              <Button variant="primary" size="sm" onClick={() => setConfirmOpen(true)} disabled={saving}>
                Review &amp; save…
              </Button>
            </>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        The per-state appeal ladder — forums (Notice, BOE, Superior court …) and the events within each. Appeal
        events key their forum + event labels off this. Ladders are statutory, so they are scoped by state rather than
        by assessor, and every county in a state inherits its ladder. Forums are retired, never deleted, so historical
        records keep their labels. {canEdit ? '' : 'Editable by a super_admin.'}
      </p>

      {templatesQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <>
          {/* State selector — the seeded ladders plus any the org has added. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-3)' }}>State</label>
            <div style={{ minWidth: 180 }}>
              <Select
                size="sm"
                options={stateKeys.map((s) => ({ value: s, label: s }))}
                value={active}
                onChange={(e) => setSelectedState(e.target.value)}
                aria-label="Select state"
              />
            </div>
            {canEdit && !addingState && (
              <Button variant="secondary" size="sm" onClick={() => setAddingState(true)}>Add state</Button>
            )}
          </div>

          {/* Add a state (§6.1). A new ladder can start from an existing state's
              framework rather than an empty list, since neighbouring states often
              share a shape and rebuilding every rung by hand is the slow path. */}
          {canEdit && addingState && (
            <div style={{
              display: 'flex', alignItems: 'flex-end', gap: 'var(--sp-2)', flexWrap: 'wrap',
              border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
              padding: 'var(--sp-3)', marginBottom: 'var(--sp-3)', background: 'var(--color-card)',
            }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                New state
                <div style={{ minWidth: 220 }}>
                  <Select
                    size="sm"
                    options={[{ value: '', label: '— Select a state —' }, ...availableStates]}
                    value={newState}
                    onChange={(e) => setNewState(e.target.value)}
                    aria-label="New state"
                  />
                </div>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                Start from
                <div style={{ minWidth: 200 }}>
                  <Select
                    size="sm"
                    options={[
                      { value: '', label: 'Empty ladder' },
                      ...stateKeys.map((s) => ({ value: s, label: `Copy ${s}` })),
                    ]}
                    value={copyFrom}
                    onChange={(e) => setCopyFrom(e.target.value)}
                    aria-label="Start from"
                  />
                </div>
              </label>
              <Button variant="primary" size="sm" onClick={addState} disabled={!newState}>Add</Button>
              <Button variant="ghost" size="sm" onClick={cancelAddState}>Cancel</Button>
              {availableStates.length === 0 && (
                <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  Every state already has a ladder.
                </p>
              )}
            </div>
          )}

          {/* Forum rows for the active state. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {forums.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--color-muted)' }}>No forums yet. Add the first rung of the ladder.</p>
            ) : forums.map((f, idx) => (
              <div
                key={idx}
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--r-md)',
                  padding: 'var(--sp-3)',
                  background: f.retired ? 'var(--color-bg)' : 'var(--color-card)',
                  opacity: f.retired ? 0.7 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <Input
                      value={f.name}
                      disabled={!canEdit}
                      aria-label={`Forum ${idx + 1} name`}
                      onChange={(e) => renameForum(idx, e.target.value)}
                    />
                  </div>
                  {f.retired && <Badge variant="warning">Retired</Badge>}
                  {canEdit && (
                    <Button variant="secondary" size="sm" onClick={() => toggleRetire(idx)}>
                      {f.retired ? 'Restore' : 'Retire'}
                    </Button>
                  )}
                </div>

                {/* Event chips — name + notifiable toggle + remove. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
                  {f.events.length === 0 ? (
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>No events.</span>
                  ) : f.events.map((e) => (
                    <span key={e.name} style={{ display: 'inline-flex' }}>
                      <Badge variant={e.notifiable ? 'success' : 'neutral'}>
                        {e.name}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => toggleNotifiable(idx, e.name)}
                            aria-label={`Toggle notify for ${e.name}`}
                            title={e.notifiable ? 'Notifies on this event — click to silence' : 'Silent — click to notify on this event'}
                            style={{
                              marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                              color: 'var(--color-ink-3)', cursor: 'pointer', fontSize: 'var(--fs-xs)',
                              lineHeight: 1, padding: 0, whiteSpace: 'nowrap',
                              display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
                            }}
                          >{e.notifiable ? <IconBell size={12} /> : <IconBellOff size={12} />}</button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => removeEvent(idx, e.name)}
                            aria-label={`Remove event ${e.name}`}
                            style={{
                              marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                              color: 'var(--color-ink-3)', cursor: 'pointer', fontSize: 'var(--fs-sm)', lineHeight: 1, padding: 0,
                            }}
                          >×</button>
                        )}
                      </Badge>
                    </span>
                  ))}
                </div>

                {canEdit && (
                  <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                    <Input
                      value={newEvent[idx] ?? ''}
                      placeholder="Add an event (e.g. decision)"
                      aria-label={`Add event to ${f.name || `forum ${idx + 1}`}`}
                      onChange={(e) => setNewEvent((m) => ({ ...m, [idx]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEvent(idx); } }}
                    />
                    <Button variant="secondary" size="sm" onClick={() => addEvent(idx)} disabled={!(newEvent[idx] ?? '').trim()}>Add event</Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
              <Input
                value={newForum}
                placeholder="Add a forum (e.g. BOE)"
                aria-label="Add a forum"
                onChange={(e) => setNewForum(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addForum(); } }}
              />
              <Button variant="secondary" size="sm" onClick={addForum} disabled={!newForum.trim()}>Add forum</Button>
            </div>
          )}

          <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            <IconBell size={12} style={{ verticalAlign: '-2px' }} /> marks an event that notifies when an appeal event
            is created for it (decisions, settlements, hearings scheduled). Retired forums stay on existing records but
            drop out of the pickers.
          </p>
        </>
      )}

      {/* §4.6 — the change CONFIRMATION. Save opens this: the diff (renamed / added /
          retired) + the blast-radius counts, so a retire that would strand live work
          is visible BEFORE the write commits. The PUT fires only on Confirm. */}
      <Modal
        open={confirmOpen}
        onClose={() => { if (!saving) setConfirmOpen(false); }}
        size="lg"
        title="Review stage-template change"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={confirmSave} disabled={saving}>
              {saving ? 'Saving…' : 'Confirm & save'}
            </Button>
          </>
        }
      >
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
          Review what this save changes and who it touches. A rename <strong>applies to history</strong>; a retired
          forum stays on existing records but drops out of the pickers. Retiring a forum that open appeals still use is
          the one change that can strand live work — check the warnings below before you confirm.
        </p>

        {diffs.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-muted)' }}>No changes to confirm.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {diffs.map((d) => (
              <div
                key={d.state}
                style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-3)' }}
              >
                <h4 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
                  {d.state}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
                  {d.renamed.map((r) => (
                    <div key={`ren-${r.from}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                      <Badge variant="info">Renamed</Badge>
                      <span>
                        <span style={{ textDecoration: 'line-through', color: 'var(--color-ink-3)' }}>{r.from}</span>
                        {' → '}
                        <strong>{r.to}</strong>
                      </span>
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>· applies to history</span>
                    </div>
                  ))}

                  {d.added.map((n) => (
                    <div key={`add-${n}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                      <Badge variant="success">Added</Badge>
                      <strong>{n}</strong>
                    </div>
                  ))}

                  {d.retired.map((n) => {
                    const strand = impact && impact.state === d.state ? impact.forums[n] : undefined;
                    return (
                      <div key={`ret-${n}`} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                          <Badge variant="warning">Retired</Badge>
                          <strong>{n}</strong>
                        </div>
                        {strand && strand.openAppeals > 0 ? (
                          <div
                            role="alert"
                            style={{
                              display: 'flex', alignItems: 'center', gap: 'var(--sp-1)', flexWrap: 'wrap',
                              padding: 'var(--sp-2)', borderRadius: 'var(--r-sm)',
                              background: 'var(--color-status-red-bg)', color: 'var(--color-status-red)',
                              fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
                            }}
                          >
                            <IconAlertTriangle size={14} />
                            <span>{strand.openAppeals.toLocaleString()} open {strand.openAppeals === 1 ? 'appeal' : 'appeals'} still use this forum</span>
                            {strand.valueRows > 0 && (
                              <span style={{ fontWeight: 'var(--fw-normal)' }}>· {strand.valueRows.toLocaleString()} value {strand.valueRows === 1 ? 'record' : 'records'}</span>
                            )}
                          </div>
                        ) : strand && strand.valueRows > 0 ? (
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                            {strand.valueRows.toLocaleString()} historical value {strand.valueRows === 1 ? 'record' : 'records'} use this forum · no open appeals
                          </div>
                        ) : impact && impact.state === d.state ? (
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>No records use this forum.</div>
                        ) : null}
                      </div>
                    );
                  })}

                  {d.restored.map((n) => (
                    <div key={`res-${n}`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                      <Badge variant="neutral">Restored</Badge>
                      <strong>{n}</strong>
                    </div>
                  ))}

                  {d.eventChanges.map((ec) => (
                    <div key={`ev-${ec.forum}`} style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                      <strong style={{ color: 'var(--color-ink-2)' }}>{ec.forum}</strong> events:
                      {ec.added.length > 0 && <span> +{ec.added.join(', ')}</span>}
                      {ec.removed.length > 0 && <span> −{ec.removed.join(', ')}</span>}
                    </div>
                  ))}

                  {d.otherChanges && (
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Notification settings updated.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Blast-radius counts for the active state (states · counties · parcels ·
            open appeals · historical records). */}
        <div style={{ marginTop: 'var(--sp-3)', paddingTop: 'var(--sp-3)', borderTop: '1px solid var(--color-border)' }}>
          <h4 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>
            Impact — {active}
          </h4>
          {impactQuery.isLoading ? (
            <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>Calculating impact…</p>
          ) : impact ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
              {[
                { label: 'States', value: impact.counts.states },
                { label: 'Counties', value: impact.counts.counties },
                { label: 'Parcels', value: impact.counts.parcels },
                { label: 'Open appeals', value: impact.counts.openAppeals },
                { label: 'Historical records', value: impact.counts.historicalRecords },
              ].map((s) => (
                <div key={s.label} style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{s.value.toLocaleString()}</span>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{s.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>Couldn't load impact counts — you can still save.</p>
          )}
        </div>
      </Modal>
    </div>
  );
}
